import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SalesOrderDetail, SalesOrderQueueCount, SalesOrderQueueQuery, SalesOrderQueueResponse } from '@nexops/shared';
import { api, apiClient } from '@/shared/lib/api';

/** Klucze React Query zamówień od handlowców (mutacja odświeża cały moduł i licznik w menu). */
export const sordKeys = {
  all: ['sales-orders'] as const,
  queue: (q: SalesOrderQueueQuery) => ['sales-orders', 'queue', q] as const,
  count: ['sales-orders', 'count'] as const,
  order: (id: string) => ['sales-orders', 'order', id] as const,
};

const silent = { silent: true } as const;

export const useOrderQueue = (q: SalesOrderQueueQuery) =>
  useQuery({
    queryKey: sordKeys.queue(q),
    queryFn: () => api.get<SalesOrderQueueResponse>('/sales-orders/queue', { status: q.status, deliveryDate: q.deliveryDate, authorId: q.authorId }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    meta: silent,
  });

export const useOrderQueueCount = (enabled: boolean) =>
  useQuery({
    queryKey: sordKeys.count,
    queryFn: () => api.get<SalesOrderQueueCount>('/sales-orders/queue/count'),
    enabled,
    refetchInterval: 60_000,
    retry: false,
    meta: silent,
  });

export const useSalesOrder = (id: string | null) =>
  useQuery({ queryKey: sordKeys.order(id ?? ''), queryFn: () => api.get<SalesOrderDetail>(`/sales-orders/${id}`), enabled: !!id, meta: silent });

function useOrderAction<V>(fn: (v: V) => Promise<SalesOrderDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (order) => qc.setQueryData(sordKeys.order(order.id), order),
    onSettled: () => void qc.invalidateQueries({ queryKey: sordKeys.all }),
  });
}

export const useEnterOrder = () => useOrderAction((id: string) => api.post<SalesOrderDetail>(`/sales-orders/${id}/entered`));

export const useRejectOrder = () => useOrderAction(({ id, reason }: { id: string; reason: string }) => api.post<SalesOrderDetail>(`/sales-orders/${id}/reject`, { reason }));

/** Arkusz zamówienia dla biura (PDF) w nowej karcie — karta otwierana od razu (blokada wyskakujących okien). */
export async function openOrderPdf(id: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const res = await apiClient.get<Blob>(`/sales-orders/${id}/pdf`, { responseType: 'blob', timeout: 60_000 });
    if (tab) tab.location.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  } catch (e) {
    tab?.close();
    throw e;
  }
}
